import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {FormattedDate} from 'react-intl';
import dayjs from '../../../mydayjs';
import {NextIcon, PreviousIcon, TickIcon, XIcon} from '../../../icons/Icons';
import useDebounce from '../../cmp/hooks/useDebounce';
import {get} from '../../../api';
import LoadingSkeleton from '../../cmp/LoadingSkeleton';
import FormattedNumber from '../../cmp/FormattedNumber';
import CurrencyNumber from '../../cmp/CurrencyNumber';
import CompPlanTeamExplorer from './CompPlanTeamExplorer';

function getTimeperiodKey(date){
  return `monthly-`+date.getMonth()+'-'+date.getFullYear()
}
const today = getTimeperiodKey(new Date())
function CompensationPlanDisplay({planId}) {
  const [timePeriodKey, setTimePeriodKey] = useState(today)
  const activeDate = useMemo(()=>{
    const parts =  timePeriodKey.split("-")
    return dayjs().month(parts[1]).year(parts[2]).toDate()
  },[timePeriodKey])

  const goPrevious = useCallback(()=>{
    setTimePeriodKey(getTimeperiodKey(dayjs(activeDate).subtract(1, 'month').toDate()))
  },[activeDate])
  const goNext = useCallback(()=>{
    setTimePeriodKey(getTimeperiodKey(dayjs(activeDate).add(1, 'month').toDate()))
  },[activeDate])

  const _timePeriodKey = useDebounce(timePeriodKey)
  const [loading, setLoading] = useState(false)
  const [result, setResult]= useState({})
  const [ignoreCache, setIgnoreCache] = useState(false)
  useEffect(()=>{
    if(!planId || !_timePeriodKey) return;
    const controller = new AbortController();
    setLoading(true)
    get('/compensationplans/v2/'+planId+'/'+_timePeriodKey, {
      ignoreCache: ignoreCache || undefined,
      rnd: ignoreCache ? Date.now() : undefined,
    }, controller)
    .then((data)=>{
      setLoading(false)
      setResult(data)
    }).catch(()=>{

    })
    return ()=>{
      controller.abort()
    }
  },[_timePeriodKey, ignoreCache, planId])
  const [activeTab, setActiveTab] = useState('report')
  return (
      <>
        <div className={"row align-items-center py-3"}>
          <div className="col-2">
            <button className="btn" onClick={goPrevious}>
              <PreviousIcon />
            </button>
          </div>
          <div onDoubleClick={()=>setIgnoreCache((x)=>!x)} className="col-8 text-center" style={{fontSize:'1.5rem'}}>
              <FormattedDate value={activeDate} year={"numeric"} month={"long"}/>
          </div>
          <div className="col-2 text-right">
            <button className="btn" disabled={today === timePeriodKey} onClick={goNext}>
              <NextIcon />
            </button>
          </div>
        </div>
        {
          result.networkExplorerConfiguration ? <>
            <ul className="nav nav-tabs mb-2">
              <li className="nav-item">
                <button
                    onClick={() => setActiveTab('report')}
                    className={`btn btn-link nav-link ${activeTab === "report" ?
                        'active' :
                        ''}`}>Report</button>
              </li>
              <li className="nav-item">
                <button
                    onClick={() => setActiveTab('network-explorer')}
                    className={`btn btn-link nav-link ${activeTab === "network-explorer" ?
                        'active' :
                        ''}`}>Network Explorer</button>
              </li>
            </ul>
          </> : null
        }
        {
          loading ? <LoadingSkeleton height={47.5} count={5}/> : <>
            {
              activeTab === "report" ?
                  <>
                    {
                      result.template ?
                          <div
                              dangerouslySetInnerHTML={{__html: result.template.value}}></div> :
                          <RenderDefault result={result}/>
                    }</> :
                  <CompPlanTeamExplorer data={result}
                                        timePeriodKey={timePeriodKey}/>
            }</>
        }
      </>
  );
}

function RenderDefault({result}){
  return    <ul className={'list-group'}>
    {
        result && result.properties && result.properties.map((i)=>{
          const val = (result.result  ?result.result[i.key] : '') || ''
          return <li className={'d-flex align-items-center justify-content-between list-group-item'} key={i.key}>
            <span>{i.name}</span>
            <span><RenderPropertyValue property={i} value={val} /></span>
          </li>
        })
    }
  </ul>
}

function RenderPropertyValue({property, value}){
  switch (property.type){
    case "currency":
      return <CurrencyNumber value={value || 0} />
    case "percentage":
      return <span><FormattedNumber value={value || 0}/>%</span>
    case "number":
      return <FormattedNumber value={value || 0} />
    case "boolean":
      return value ? <span className="text-success"><TickIcon /></span> : <span className="text-danger"><XIcon /></span>
    default:
      return value
  }
}

export default CompensationPlanDisplay;
